import React, { useState } from "react";
import { Calculator, MapPin, Clock, Phone, Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuickQuote = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    pickupCity: "",
    destinationCity: "",
    transportationType: "",
    tripType: "",
    additionalInfo: ""
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/quick-quote`, formData);
      
      if (response.data.success) {
        toast.success("Quote request submitted! We'll contact you with pricing within 24 hours.");
        // Reset form
        setFormData({
          name: "",
          phone: "",
          email: "",
          pickupCity: "",
          destinationCity: "",
          transportationType: "",
          tripType: "",
          additionalInfo: ""
        });
      }
    } catch (error) {
      console.error("Quote submission error:", error);
      const errorMessage = error.response?.data?.detail || 
                          "There was an error submitting your quote request. Please call us at (269) 248-3514.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Get a Quick Quote</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Get an estimate for your transportation needs. We'll provide pricing information 
            within 24 hours for both insurance-covered and self-pay options.
          </p>
        </div>

        {/* Benefits */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="text-center">
            <CardContent className="p-6">
              <Calculator className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">Fast Response</h3>
              <p className="text-gray-600 text-sm">Receive your quote within 24 hours</p>
            </CardContent>
          </Card>
          
          <Card className="text-center">
            <CardContent className="p-6">
              <MapPin className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">Any Distance</h3>
              <p className="text-gray-600 text-sm">Local and long-distance pricing available</p>
            </CardContent>
          </Card>
          
          <Card className="text-center">
            <CardContent className="p-6">
              <Phone className="h-12 w-12 text-purple-600 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">No Obligation</h3>
              <p className="text-gray-600 text-sm">Free quote with no commitment required</p>
            </CardContent>
          </Card>
        </div>

        {/* Quote Form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl text-gray-900">Quote Request Form</CardTitle>
            <p className="text-gray-600">
              Provide basic information to receive your transportation quote
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Contact Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    placeholder="Your name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    placeholder="(269) 123-4567"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  placeholder="your.email@example.com (optional)"
                />
              </div>

              {/* Trip Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="pickupCity">Pickup Location *</Label>
                  <Input
                    id="pickupCity"
                    type="text"
                    value={formData.pickupCity}
                    onChange={(e) => handleInputChange("pickupCity", e.target.value)}
                    placeholder="City, State (e.g., Battle Creek, MI)"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="destinationCity">Destination *</Label>
                  <Input
                    id="destinationCity"
                    type="text"
                    value={formData.destinationCity}
                    onChange={(e) => handleInputChange("destinationCity", e.target.value)}
                    placeholder="City, State (e.g., Kalamazoo, MI)"
                    required
                  />
                </div>
              </div>

              {/* Transportation Type */}
              <div className="space-y-3">
                <Label>Transportation Type *</Label>
                <RadioGroup
                  value={formData.transportationType}
                  onValueChange={(value) => handleInputChange("transportationType", value)}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="wheelchair" id="wheelchair" />
                    <Label htmlFor="wheelchair">Wheelchair Accessible Vehicle</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="ambulatory" id="ambulatory" />
                    <Label htmlFor="ambulatory">Ambulatory (Walking) Transportation</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="both" id="both" />
                    <Label htmlFor="both">Need Both Options</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Trip Type */}
              <div className="space-y-2">
                <Label htmlFor="tripType">Trip Type *</Label>
                <Select value={formData.tripType} onValueChange={(value) => handleInputChange("tripType", value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select trip type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one-way">One-Way Trip</SelectItem>
                    <SelectItem value="round-trip">Round-Trip (Wait & Return)</SelectItem>
                    <SelectItem value="multiple">Multiple Stops</SelectItem>
                    <SelectItem value="recurring">Recurring Appointments</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Additional Information */}
              <div className="space-y-2">
                <Label htmlFor="additionalInfo">Additional Information</Label>
                <Input
                  id="additionalInfo"
                  type="text"
                  value={formData.additionalInfo}
                  onChange={(e) => handleInputChange("additionalInfo", e.target.value)}
                  placeholder="Special equipment, frequency, or other details"
                />
              </div>

              {/* Submit Button */}
              <div className="pt-6">
                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full bg-blue-900 hover:bg-blue-800"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Submitting Request..." : "Get My Quote"}
                </Button>
                <p className="text-sm text-gray-500 mt-3 text-center">
                  We'll contact you within 24 hours with pricing for both insurance-covered 
                  and self-pay options.
                </p>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Contact Alternative */}
        <div className="mt-8 text-center">
          <p className="text-gray-600 mb-4">Prefer to speak with someone directly?</p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-6">
            <div className="flex items-center space-x-2">
              <Phone className="h-4 w-4 text-blue-900" />
              <a href="tel:(269) 248-3514" className="font-semibold text-blue-900 hover:text-blue-700">
                (269) 248-3514
              </a>
            </div>
            <div className="flex items-center space-x-2">
              <Mail className="h-4 w-4 text-blue-900" />
              <a href="mailto:qualitytransportservices42@gmail.com" className="text-blue-900 hover:text-blue-700">
                qualitytransportservices42@gmail.com
              </a>
            </div>
          </div>
          <div className="mt-4 text-sm text-gray-500">
            <Clock className="h-4 w-4 inline mr-1" />
            Mon-Thu: 8am-5pm | Fri: 8am-4pm | Sat-Sun: 8am-12pm
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickQuote;